// Gölge emir uçları. Karşılıkları: src/shadow/api/app.py
//
// Emir açıldıktan sonra durum ELDE GÜNCELLENMİYOR: sunucu emir listesi
// değiştiğinde bir sonraki turda WebSocket'ten tam listeyi yolluyor. Tek kaynak
// böylece soket olarak kalıyor — iki ayrı güncelleme yolu birbiriyle yarışmıyor.

import type { OrderHistory, ShadowOrder, Side } from './types'

export interface OrderRequest {
  contract: string
  side: Side
  price: number
  quantity: number
}

/** Sunucunun hata gövdesinden okunabilir mesaj çıkar. */
async function hataMesaji(response: Response): Promise<string> {
  try {
    const body = await response.json()
    const detail = body?.detail
    if (typeof detail === 'string') return detail
    // Pydantic doğrulama hatası: [{loc, msg, ...}]
    if (Array.isArray(detail) && detail.length) {
      return detail.map((d) => d?.msg ?? '').filter(Boolean).join(', ') || 'Geçersiz emir'
    }
  } catch {
    /* gövde JSON değil — genel mesaja düş */
  }
  return `İstek başarısız (${response.status})`
}

export async function placeOrder(request: OrderRequest): Promise<ShadowOrder> {
  const response = await fetch('/api/orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/**
 * Hızlı Al/Sat: verilen fiyata KADAR karşı taraftaki açık lotları süpür.
 * `quantity` verilmezse "ne varsa"; verilirse SUNUCU mevcutla sınırlar —
 * istemcinin gördüğü sayı 1 sn bayat olabilir; artık emir pasife yatmaz.
 */
export async function quickSweep(request: {
  contract: string
  side: Side
  price: number
  quantity?: number
}): Promise<ShadowOrder> {
  const response = await fetch('/api/orders/sweep', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/**
 * Emri düzenle. Kimlik DEĞİŞMEZ, sunucuda yeni bir sürüm oluşur ve eski hâl
 * kalıcı kayıtta durur. Verilmeyen alan olduğu gibi kalır.
 */
export async function amendOrder(
  orderId: string,
  patch: { price?: number; quantity?: number },
): Promise<ShadowOrder> {
  const response = await fetch(`/api/orders/${encodeURIComponent(orderId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/** "Sil" emri gerçekten silmez: durumu CANCELLED olur, geçmişi kalıcı kayıtta kalır. */
export async function cancelOrder(orderId: string): Promise<ShadowOrder> {
  const response = await fetch(`/api/orders/${encodeURIComponent(orderId)}`, {
    method: 'DELETE',
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/**
 * Formül test aracı (`Değerlendir`): ifadeyi sunucudaki motor, seçilen
 * kontratın canlı verisiyle çalıştırır. Botların kullanacağı motorun aynısı.
 */
export async function evaluateExpression(request: {
  contract: string
  expression: string
}): Promise<{ value: number; keywords: string[] }> {
  const response = await fetch('/api/bots/evaluate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

// ── Botlar ───────────────────────────────────────────────────────────────────
export interface BotOut {
  id: string
  type: string
  contract: string
  params: Record<string, string>
  active: boolean
  /** Kontrat kapandı — bot artık çalışamaz ve yeniden başlatılamaz. */
  expired: boolean
  createdAt: number
  bought: number
  sold: number
  net: number
  /** BEST botları: amount hedefi tamamlandı — bot kapalı konumda, teklif atmıyor. */
  done: boolean
  /**
   * Kaldırılmış bot. Yalnız `includeRemoved` ile gelir: yönetici onu bellekten
   * düşürdüğü için canlı alanları yoktur (emirleri iptal edildi, hatası olamaz).
   */
  removed: boolean
  openOrders: { id: string; side: Side; price: number; quantity: number; filled: number }[]
  lastError: string | null
}

/**
 * @param includeRemoved Kaldırılmış botları da getir. Varsayılan dışında:
 *   sunucu onları her istekte veritabanından okuyor (bellekte yoklar).
 */
export async function fetchBots(contract?: string, includeRemoved = false): Promise<BotOut[]> {
  const params = new URLSearchParams()
  if (contract) params.set('contract', contract)
  if (includeRemoved) params.set('includeRemoved', 'true')
  const qs = params.size ? `?${params}` : ''
  const response = await fetch(`/api/bots${qs}`)
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/** Bot kur ve hemen çalıştır — bir sonraki akış turunda tahtaya çıkar. */
export async function createBot(request: {
  type: string
  contract: string
  params: Record<string, string>
}): Promise<BotOut> {
  const response = await fetch('/api/bots', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/** Duraklat/sürdür. Duraklayan botun emirleri tahtada KALIR (bilerek). */
export async function setBotActive(botId: string, active: boolean): Promise<BotOut> {
  const response = await fetch(`/api/bots/${encodeURIComponent(botId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/**
 * Stratejiyi değiştir — bot kimliği, tipi, kontratı ve geçmişi korunur;
 * bot artık yeni parametrelerle teklif atar (emir düzenlemesiyle aynı felsefe).
 */
export async function updateBotParams(
  botId: string,
  params: Record<string, string>,
): Promise<BotOut> {
  const response = await fetch(`/api/bots/${encodeURIComponent(botId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ params }),
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/** Botu kaldır: açık emirleri iptal edilir, kaydı REMOVED olarak kalıcıda kalır. */
export async function removeBot(botId: string): Promise<void> {
  const response = await fetch(`/api/bots/${encodeURIComponent(botId)}`, {
    method: 'DELETE',
  })
  if (!response.ok) throw new Error(await hataMesaji(response))
}

/** GÖP çipinin durumu — canlı GÖP eşleşmesi + içeri alınmış kayıt. */
export interface DamStatus {
  contract: string
  /** GainSpark bağlantısı tanımlı mı — değilse özellik kapalı. */
  configured: boolean
  /** Kalıcı kayıt açık mı — kapalıysa içeri alma saklanamaz. */
  persisted: boolean
  /** Canlı GÖP eşleşmesi (MWh); null = veri yok ya da kaynağa ulaşılamadı. */
  live: { bids: number; offers: number; mwh: number } | null
  /** İçeri alınacak maliyet tabanı (PTF); null = bilinmiyor, alma reddedilir. */
  ptf: number | null
  /** İçeri alınmış kayıt; null = dışarıda. */
  imported: { mwh: number; ptf: number; importedAt: string } | null
}

export async function fetchDamStatus(contract: string): Promise<DamStatus> {
  const response = await fetch(`/api/dam/${encodeURIComponent(contract)}`)
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}

/** GÖP pozisyonunu içeri al — NETPC/DAMQC/PnL o andan itibaren içerir. */
export async function importDam(contract: string): Promise<void> {
  const response = await fetch(`/api/dam/${encodeURIComponent(contract)}`, { method: 'POST' })
  if (!response.ok) throw new Error(await hataMesaji(response))
}

/** GÖP pozisyonunu çıkar — etkisi iz bırakmadan kalkar. */
export async function removeDam(contract: string): Promise<void> {
  const response = await fetch(`/api/dam/${encodeURIComponent(contract)}`, { method: 'DELETE' })
  if (!response.ok) throw new Error(await hataMesaji(response))
}

/** Emrin tüm hayatı — info modalı bunu gösteriyor. */
export async function fetchOrderHistory(orderId: string): Promise<OrderHistory> {
  const response = await fetch(`/api/orders/${encodeURIComponent(orderId)}/history`)
  if (!response.ok) throw new Error(await hataMesaji(response))
  return response.json()
}
